import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../apiConfig";


const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }
  return context;
};

export const WishlistProvider = ({ children, token }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (token) {
      fetchWishlist();
    } else {
      setWishlistItems([]);
      setWishlistIds(new Set());
    }
  }, [token]);

  const fetchWishlist = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wishlist`, { headers });
      setWishlistItems(res.data.products || []);
      setWishlistIds(new Set(res.data.products?.map(p => p.product_id) || []));
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToWishlist = async (productId) => {
    if (!token) {
      toast.error("Please sign in to add to wishlist");
      return false;
    }

    try {
      await axios.post(`${API}/wishlist/add/${productId}`, {}, { headers });
      setWishlistIds(prev => new Set([...prev, productId]));
      toast.success("Added to wishlist");
      fetchWishlist(); // Refresh full list
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to wishlist");
      return false;
    }
  };

  const removeFromWishlist = async (productId) => {
    if (!token) return false;

    try {
      await axios.delete(`${API}/wishlist/remove/${productId}`, { headers });
      setWishlistIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
      setWishlistItems(prev => prev.filter(p => p.product_id !== productId));
      toast.success("Removed from wishlist");
      return true;
    } catch (error) {
      toast.error("Failed to remove from wishlist");
      return false;
    }
  };

  const toggleWishlist = async (productId) => {
    if (wishlistIds.has(productId)) {
      return removeFromWishlist(productId);
    } else {
      return addToWishlist(productId);
    }
  };

  const isInWishlist = (productId) => wishlistIds.has(productId);

  const moveToCart = async (productId) => {
    if (!token) return false;

    try {
      await axios.post(`${API}/wishlist/move-to-cart/${productId}`, {}, { headers });
      setWishlistIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
      setWishlistItems(prev => prev.filter(p => p.product_id !== productId));
      toast.success("Moved to cart");
      return true;
    } catch (error) {
      toast.error("Failed to move to cart");
      return false;
    }
  };

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      wishlistCount: wishlistIds.size,
      loading,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isInWishlist,
      moveToCart,
      refreshWishlist: fetchWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistProvider;
